import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTheme } from '@/contexts/ThemeContext';
import { layout4ThemeStyle } from './theme';
import { ServiceModal } from '@/components/ServiceModal';
import { openWhatsApp } from '@/lib/whatsapp';
import { useStore } from '@/contexts/StoreContext';
import {
  MapPin,
  Mail,
  Instagram,
  Facebook,
  Youtube
} from 'lucide-react';

import googlePlay from '@/assets/google-play.png';
import appStore from '@/assets/app-store.png';
import mobiautoIcon from '@/assets/mobiauto_icon.png';
import { StoreFooterIcon } from '@/components/StoreFooterIcon';
import { hasAppStoreLink as hasResolvedAppStoreLink, resolveAppStoreUrl } from '@/lib/appStoreLinks';
import './styles.css';

interface FooterProps {
  config: any;
}

const CONSIGNMENT_SERVICE_ID = '65dcb7c58eca108d87c91dbd';
const VALUATION_SERVICE_ID = '65dcb7548eca108d87c91dbb';

export const Footer = ({ config }: FooterProps) => {
  const { selectedStore } = useStore();
  const store = selectedStore || config.stores[0];
  const { colors } = useTheme();
  const themeStyle = layout4ThemeStyle(colors);
  const menuColor = colors.menu;
  const headerColor = colors.header;
  const buttonColor = colors.button;
  const [selectedService, setSelectedService] = useState<any>(null);
  const [sellCtaService, setSellCtaService] = useState<any>(null);
  const hasGooglePlayLink = !!config.googlePlay?.url?.trim();
  const appStoreUrl = resolveAppStoreUrl(config.appStore);
  const hasAppStoreLink = hasResolvedAppStoreLink(config.appStore);

  const quickLinks = [
    { label: 'Início', href: '/' },
    { label: 'Estoque', href: '/estoque' },
    { label: 'Zero KM', href: '/estoque?type=zerokm' },
    { label: 'Seminovos', href: '/estoque?type=seminovo' },
    { label: 'Favoritos', href: '/favoritos' },
    { label: 'Serviços', href: '/servicos' },
    { label: 'Contato', href: '/contato' }
  ];

  const getServiceName = (serviceId: string) => {
    const serviceNames: Record<string, string> = {
      '65da42048eca108d87c91da6': 'Agendar Videochamada',
      '65dcb7548eca108d87c91dbb': 'Avaliação',
      '65dcb7c58eca108d87c91dbd': 'Consignação',
      '65dcef468eca108d87c91dc8': 'Consórcio',
      '65dcb6528eca108d87c91db9': 'Despachante',
      '65dcb7898eca108d87c91dbc': 'Financiamento',
      '6628fd535bda2ae51f6fc5ff': 'Peças e Acessórios',
      '65dcef098eca108d87c91dc7': 'Revisões',
      '65dcb7238eca108d87c91dba': 'Seguros',
      '65dceeac8eca108d87c91dc6': 'Serviços',
      '65dcb7fe8eca108d87c91dbe': 'Tabela FIPE',
      '65dcb5e78eca108d87c91db8': 'Test-Drive',
      '65dcb8548eca108d87c91dbf': 'Vendas Diretas'
    };

    return serviceNames[serviceId] || 'Serviço';
  };

  const servicesList = store.services || [];

  const sellService =
    servicesList.find((s: any) => s.service === CONSIGNMENT_SERVICE_ID) ||
    servicesList.find((s: any) => s.service === VALUATION_SERVICE_ID) ||
    null;

  const handleSellCta = () => {
    if (sellService) {
      setSellCtaService(sellService);
    } else if (store.whatsapp?.ios) {
      const message = encodeURIComponent('Olá! Gostaria de vender meu veículo com vocês.');
      const url = store.whatsapp.ios.includes('text=')
        ? store.whatsapp.ios.replace(/text=[^&]*/, `text=${message}`)
        : `${store.whatsapp.ios}&text=${message}`;
      openWhatsApp(url);
    }
  };

  const brandDescription =
    config.about?.split('\n')[0]?.slice(0, 160) ||
    config.slogan ||
    'Seminovos e zero KM com atendimento personalizado e as melhores condições do mercado.';

  const socialLinks = [
    { key: 'facebook', url: config.socialNetworks?.facebook, Icon: Facebook, label: 'Facebook' },
    { key: 'instagram', url: config.socialNetworks?.instagram, Icon: Instagram, label: 'Instagram' },
    { key: 'youtube', url: config.socialNetworks?.youtube, Icon: Youtube, label: 'YouTube' },
  ].filter((s) => !!s.url);

  return (
    <footer className="layout-4 wl4-footer" style={themeStyle}>
      {/* CTA strip — mesmas cores da nav do header (fundo header, texto menu, botão primary) */}
      <div className="wl4-footer-cta" style={{ backgroundColor: headerColor }}>
        <div className="wl4-container wl4-footer-cta-inner">
          <h2 className="wl4-footer-cta-title" style={{ color: menuColor }}>
            Tem algum <i>veículo</i> para vender?
          </h2>
          <button
            type="button"
            className="wl4-nav-cta wl4-footer-cta-btn"
            style={{ backgroundColor: buttonColor }}
            onClick={handleSellCta}
          >
            Venda seu carro hoje
          </button>
        </div>
      </div>

      {/* Main columns */}
      <div className="wl4-footer-main">
        <div className="wl4-container py-14 md:py-16">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-10 lg:gap-8">
            {/* Brand + contact */}
            <div className="lg:pr-4">
              <Link to="/" className="inline-block mb-5">
                <div className="wl4-footer-brand">
                  <StoreFooterIcon
                    config={config}
                    store={store}
                    alt={config.name}
                    className="wl4-footer-brand-img"
                  />
                </div>
              </Link>
              <p className="wl4-footer-desc">{brandDescription}</p>

              {store?.place?.address && (
                <div className="wl4-footer-contact-item">
                  <MapPin className="wl4-footer-contact-icon" />
                  <span>{store.place.address}</span>
                </div>
              )}

              {config.email && (
                <div className="wl4-footer-contact-item">
                  <Mail className="wl4-footer-contact-icon" />
                  <a href={`mailto:${config.email}`}>{config.email}</a>
                </div>
              )}

              {(hasGooglePlayLink || hasAppStoreLink) && (
                <div className="flex flex-wrap gap-2 mt-6">
                  {hasGooglePlayLink && (
                    <a href={config.googlePlay.url} target="_blank" rel="noopener noreferrer">
                      <img src={googlePlay} alt="Disponível no Google Play" width={120} height={36} className="h-9 w-auto" />
                    </a>
                  )}
                  {hasAppStoreLink && (
                    <a href={appStoreUrl} target="_blank" rel="noopener noreferrer">
                      <img src={appStore} alt="Baixar na App Store" width={120} height={36} className="h-9 w-auto" />
                    </a>
                  )}
                </div>
              )}
            </div>

            {/* Quick Links */}
            <div>
              <h4 className="wl4-footer-col-title">Links rápidos</h4>
              <ul className="wl4-footer-links">
                {quickLinks.map((link) => (
                  <li key={link.href}>
                    {link.href.startsWith('#') ? (
                      <a href={link.href}>{link.label}</a>
                    ) : (
                      <Link to={link.href}>{link.label}</Link>
                    )}
                  </li>
                ))}
              </ul>
            </div>

            {/* Services */}
            <div>
              <h4 className="wl4-footer-col-title">Serviços</h4>
              <ul className="wl4-footer-links">
                {servicesList.length > 0 ? (
                  servicesList.map((service: any) => (
                    <li key={service.service}>
                      <button type="button" onClick={() => setSelectedService(service)}>
                        {getServiceName(service.service)}
                      </button>
                    </li>
                  ))
                ) : (
                  <li>
                    <Link to="/servicos">Ver serviços</Link>
                  </li>
                )}
              </ul>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom bar */}
      <div className="wl4-footer-bottom">
        <div className="wl4-container wl4-footer-bottom-inner">
          <p className="wl4-footer-copyright">
            &copy; {new Date().getFullYear()} {config.name}. Todos os direitos reservados.
          </p>

          {socialLinks.length > 0 && (
            <div className="wl4-footer-social">
              {socialLinks.map(({ key, url, Icon, label }) => (
                <a
                  key={key}
                  href={url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="wl4-footer-social-btn"
                  aria-label={label}
                >
                  <Icon className="h-4 w-4" />
                </a>
              ))}
            </div>
          )}

          <div className="wl4-footer-legal">
            <Link to="/politica-privacidade">Política de Privacidade</Link>
            <Link to="/termos-uso">Termos de Uso</Link>
            <Link to="/contato">Contato</Link>
          </div>
        </div>
      </div>

      {selectedService && (
        <ServiceModal
          service={selectedService}
          config={config}
          isOpen={!!selectedService}
          onOpenChange={(open) => {
            if (!open) setSelectedService(null);
          }}
        />
      )}

      {sellCtaService && (
        <ServiceModal
          service={sellCtaService}
          config={config}
          isOpen={!!sellCtaService}
          onOpenChange={(open) => {
            if (!open) setSellCtaService(null);
          }}
        />
      )}
    </footer>
  );
};
